import React, { useEffect, useState } from "react";
import { Crown, Calendar, CreditCard, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useRazorpay } from "@/hooks/useRazorpay";

export default function Subscription() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(false);

  useEffect(() => {
    api.get("/billing/subscription").then(r => setSub(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const rzpLoaded = useRazorpay();

  const renew = async (planId) => {
    if (!rzpLoaded) { toast.error("Payment Gateway is still loading..."); return; }
    setRenewing(true);
    try {
      // Step 1: Create order
      const { data: orderData } = await api.post("/billing/subscription/renew", { plan: planId });
      
      if (orderData.mock) {
        // Fallback for mock environments
        const { data } = await api.post("/billing/subscription/verify", { plan: planId, mock: true });
        toast.success(`Plan activated until ${data.plan_end}!`);
        api.get("/billing/subscription").then(r => setSub(r.data));
        setRenewing(false);
        return;
      }

      // Step 2: Open Razorpay Checkout
      const options = {
        key: orderData.platform_key_id,
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: "DishLCO SaaS",
        description: orderData.message,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            // Step 3: Verify Payment
            const { data } = await api.post("/billing/subscription/verify", {
              plan: planId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success(`Plan activated until ${data.plan_end}!`);
            api.get("/billing/subscription").then(r => setSub(r.data));
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          }
        },
        theme: { color: "#3B82F6" },
        modal: {
          ondismiss: function() {
            setRenewing(false);
            toast.info("Payment cancelled");
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (e) { 
      toast.error(e?.response?.data?.detail || "Renewal failed"); 
      setRenewing(false);
    }
  };

  if (loading) return <p className="text-slate-400 pt-4">Loading…</p>;

  const statusColor = {
    active: "bg-green-50 text-green-700 border-green-200",
    grace: "bg-amber-50 text-amber-700 border-amber-200",
    expired: "bg-red-50 text-red-700 border-red-200",
    none: "bg-slate-50 text-slate-500 border-slate-200",
  };

  return (
    <div className="space-y-6">
      <h3 className="font-heading font-bold text-2xl text-brand-nav">My Subscription</h3>

      {/* Current Plan Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <Crown className="w-8 h-8 text-brand-primary mb-2" />
            <h4 className="font-heading font-bold text-xl text-brand-nav capitalize">{sub?.plan || "No Plan"}</h4>
          </div>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${statusColor[sub?.display_status] || statusColor.none}`}>
            {sub?.display_status || "none"}
          </span>
        </div>
        {sub?.plan_end && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span>Ends: <strong>{sub.plan_end}</strong></span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CreditCard className="w-4 h-4 text-slate-400" />
              <span>Paid: <strong>₹{sub.amount_paid?.toLocaleString()}</strong></span>
            </div>
          </div>
        )}
        {sub?.days_remaining !== undefined && (
          <div className="mt-4 bg-slate-50 rounded-lg p-3">
            <div className="flex justify-between text-xs text-slate-500">
              <span>Days remaining</span><span className="font-bold">{sub.days_remaining}</span>
            </div>
            <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-brand-primary to-brand-accent rounded-full transition-all"
                style={{ width: `${Math.min(100, (sub.days_remaining / 365) * 100)}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Plan Options */}
      <h4 className="font-heading font-bold text-lg text-brand-nav">Renew / Upgrade</h4>
      <div className="grid md:grid-cols-3 gap-4">
        {(sub?.plan_options || []).map(p => (
          <div key={p.id} className={`bg-white rounded-xl border-2 p-5 transition-all ${sub?.plan === p.id ? "border-brand-primary" : "border-slate-200 hover:border-slate-300"}`}>
            <p className="font-heading font-bold text-lg text-brand-nav capitalize">{p.label}</p>
            <p className="font-heading font-bold text-3xl text-brand-accent mt-2">₹{p.amount.toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-1">{p.days} days</p>
            <button onClick={() => renew(p.id)} disabled={renewing}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-50 transition-colors">
              {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {sub?.plan === p.id ? "Renew" : "Switch Plan"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
